import { performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import { useFieldConfig } from '@atlassian/jira-issue-field-base/src/services/field-config-service/main.tsx';
import type { AnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	toString,
	type FieldId,
	type FieldValue,
	type UseResolveFieldValuesByIdResultType,
	type UseResolveFieldValuesByIdType,
	type ValueType,
} from '../../../types/index.tsx';

type Props<T> = {
	fieldId: FieldId;
	useResolveFieldValuesById: UseResolveFieldValuesByIdType<T>;
	onSuccess?: (arg1: FieldValue[]) => void;
	onFailure?: (arg1: Error) => void;
};

type ReturnType<T> = [
	{
		isLoading: boolean;
		data: UseResolveFieldValuesByIdResultType<T>[] | undefined;
		error: Error | undefined;
	},
	{
		saveValue?: (
			values: ValueType<T>[],
			meta: null,
			analyticsEvent: AnalyticsEvent,
		) => Promise<void>;
	},
];

const getFieldUpdate = (fieldId: string, values: { id: string }[]) => ({
	fields: {
		[fieldId]: values,
	},
});

const saveField = async (issueKey: IssueKey, fieldId: string, newValue: FieldValue[]) =>
	// eslint-disable-next-line jira/js/no-jira-fetch
	performPutRequest(`/rest/api/3/issue/${issueKey}/`, {
		body: JSON.stringify(getFieldUpdate(fieldId, newValue)),
	});

const toFieldValue = <T,>(values: ValueType<T>[]) => values.map((x) => ({ id: x.id }));

/**
 * This hook is used to translate the ids provided by Bento in to the corresponding values.
 * To do this it uses an injected hook `useResolveFieldValuesById` which does the actual
 * translation.
 */
export const useCollapsibleField = <T,>({
	fieldId,
	onSuccess,
	onFailure,
	useResolveFieldValuesById,
}: Props<T>): ReturnType<T> => {
	const issueKey = useIssueKey();
	const [{ value: fieldConfig }] = useFieldConfig(issueKey, toString(fieldId));

	const fieldType = fieldConfig?.schema.type ?? 'array';

	// Retrieves the current field values as stored in Bento (IDs)
	const [{ value, error: errorEditField }, { saveValue }] = useEditField<FieldValue[], null>({
		fieldKey: toString(fieldId),
		issueKey,
		fieldType,
		initialValue: [],
		onSuccess,
		onFailure,
		saveField,
	});

	// Resolve the Bento field values using the passed in resolver
	// eslint-disable-next-line react-hooks/react-compiler
	const { isLoading, data, error: fieldValueError } = useResolveFieldValuesById(fieldId, value);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const save = (values: ValueType<T>[], meta: any, analyticsEvent: AnalyticsEvent) =>
		saveValue(toFieldValue(values), meta, analyticsEvent);

	return [{ isLoading, data, error: errorEditField || fieldValueError }, { saveValue: save }];
};
